import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { TEST_EQUIPMENT_CONTEXT_ENUM } from "@shared/enums/test-equipment-context.enum";
import { TEST_EQUIPMENT_DEVICE_SELECTION, TEST_EQUIPMENT_DEVICE_SELECTION_VALUE } from "@shared/enums/test-equipment-device-selection.enum";
import { ERROR_FUNCTION_TYPE } from "@shared/enums/test-equipment-error-function-type.enum";
import { DEVICE_TYPE_LINEAR_RANGE_ENUM } from "@shared/enums/device-type-linear-range.enum";
import { ATHLETE_EQUIPMENT_CATEGORIES, TEST_EQUIPMENT_DEVICE_LIST } from "@shared/enums/test-equipment-device-list.enum";
import { DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM } from "@shared/enums/device-type-exponential-range.enum";
import { SessionService } from '@core/services/session.service';

@Component({
  selector: 'app-test-equipment',
  templateUrl: './test-equipment.component.html',
  styleUrls: ['./test-equipment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestEquipmentComponent implements OnInit {
  private fb: FormBuilder = inject(FormBuilder);
  private sessionService: SessionService = inject(SessionService);

  public TYPE = TEST_EQUIPMENT_CONTEXT_ENUM;
  public ERROR_FUNCTION_ENUM = ERROR_FUNCTION_TYPE;
  public form: FormGroup;
  public optionList: any[] = [];
  public errorFunctionOptionList: any[] = [];
  public errorFunctionOptionList2: any[] = [];
  public referenceArray: any[] = [];
  public isShowErrorFunction: boolean = false;
  public linearDefaultValue: any;
  public linearDefaultValue2: any;
  public exponentialDefaultValue: any;
  public exponentialDefaultValue2: any;
  public isLock: boolean = false;
  public isLock2: boolean = false;
  public currentDeviceId: number;
  public selectedDevice: any;
  public isShowErrorFunction2: boolean = false;
  public deviceSelectionList: any = [
    {
      name: "Use athlete's device",
      subName: 'No default assigned',
      value: 0,
      disabled: true,
    },
    {
      name: "Use organization's device",
      subName: 'No default assigned',
      value: 1,
      disabled: true,
    },
    {
      name: 'Use another device',
      subName: 'Select below',
      value: 2,
      disabled: false,
    },
  ];

  @Input() public isExpanded: boolean = false;
  @Input() public config: any;
  @Input() public context: any;
  @Input() public equipmentDeviceList: any[];
  @Output() public onChange: EventEmitter<any> = new EventEmitter<any>();

  public ngOnInit(): void {
    const selectedRadioButton = this.getSelectedRadioButton(this.config);
    this.selectedDevice = this.getSelectedDevice(this.config, selectedRadioButton);
    this.initForm(this.selectedDevice);
    this.initOptionAndValueForm(this.config, selectedRadioButton);
    this.setAccessibilityInputs(this.config);
    this.setAccessibilityInputs2(this.config);
    this.disableInputs();
    this.disableInputs2();
    this.setValidators();
    this.setValidators2(this.equipmentDeviceList);
    this.setLock(this.selectedDevice);
    this.setLock2(this.selectedDevice, this.equipmentDeviceList);
    if (this.selectedDevice) {
      this.setReferences(this.equipmentDeviceList, this.selectedDevice);
    }

    if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      // this.trackSelectedDevice();
      if (this.selectedDevice) {
        const deviceData = this.form.value;
        // If the combobox is disabled it's value is not included in `this.form.value`
        deviceData.id = this.form.get('id')?.value;
        this.onChange.emit({
          data: deviceData,
          category: this.selectedDevice.category,
          name: this.selectedDevice.name,
        });
      }
    }
  }

  private getSelectedRadioButton(config: any): number {
    if (config.athleteDefault != null) {
      return 0
    } else if (config.organizationDefault != null) {
      return 1
    } else {
      return 2
    }
  }

  public setValidators(): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);

    if (selectedDevice) {
      if (selectedDevice.selected_error_function === ERROR_FUNCTION_TYPE.LINEAR) {
        this.setValidationOnLinear(selectedDevice);
      } else if (selectedDevice.selected_error_function === ERROR_FUNCTION_TYPE.EXPONENTIAL) {
        this.setValidationOnExponential(selectedDevice);
      }
    }
  }

  public setValidators2(originalDeviceList: any[]): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);

    if (selectedDevice) {
      const secondDevice: any = originalDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);

      if (secondDevice) {
        if (secondDevice.selected_error_function === ERROR_FUNCTION_TYPE.LINEAR) {
          this.setValidationOnLinear2(secondDevice);
        }
      }
    }
  }

  public onInputChanged(): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);

    setTimeout(() => {
      this.onChange.emit({
        data: this.form.value,
        category: selectedDevice.category,
        name: selectedDevice.name,
      });
    });
  }

  public onInputChanged2(): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);
    const secondDevice: any = this.equipmentDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);

    setTimeout(() => {
      this.onChange.emit({
        data: this.form.value,
        category: selectedDevice.category,
        name: selectedDevice.name,
      });
    });
  }

  public onComboBoxChanged(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, event.value);
    let hasSecondErrorFunction: any = false;

    if (selectedDevice) {
      hasSecondErrorFunction = this.hasSecondErrorFunction(this.equipmentDeviceList, selectedDevice)
    }


    if (hasSecondErrorFunction && selectedDevice.category === TEST_EQUIPMENT_DEVICE_LIST.VO2_ANALYZER) {
      const secondDevice: any = this.equipmentDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);


      this.form.addControl('selected_error_function2', new FormControl('', Validators.required));
      this.form.addControl('a_slope2', new FormControl('', Validators.required));
      this.form.addControl('b_offset2', new FormControl('', Validators.required));
      this.form.addControl('a2', new FormControl('', Validators.required));
      this.form.addControl('b2', new FormControl('', Validators.required));
      this.form.addControl('c2', new FormControl('', Validators.required));
      this.form.addControl('d2', new FormControl('', Validators.required));
      this.setValidators2(this.equipmentDeviceList);
      this.setSecondFormValue(this.equipmentDeviceList, secondDevice);
    }

    if (selectedDevice.hasOwnProperty('linear_function')) {
      this.setDefaultLinearValue(selectedDevice);
    }

    if (selectedDevice.hasOwnProperty('exponential_function')) {
      this.setDefaultExponentialValue(selectedDevice);
    }

    this.setReferences(this.equipmentDeviceList, selectedDevice);
    this.setErrorFunctionOptionList(this.config, event.value);
    this.form.get('selected_error_function')?.setValue(selectedDevice.selected_error_function);
    this.form.get('organization')?.setValue(false);
    if (selectedDevice.selected_error_function === ERROR_FUNCTION_TYPE.LINEAR) {
      this.setValidationOnLinear(selectedDevice);
      this.form.get('a_slope')?.setValue(selectedDevice.linear_function.a);
      this.form.get('b_offset')?.setValue(selectedDevice.linear_function.b);
    } else if (selectedDevice.selected_error_function === ERROR_FUNCTION_TYPE.EXPONENTIAL) {
      this.setValidationOnExponential(selectedDevice);
      this.form.get('a')?.setValue(selectedDevice.exponential_function.a);
      this.form.get('b')?.setValue(selectedDevice.exponential_function.b);
      this.form.get('c')?.setValue(selectedDevice.exponential_function.c);
      this.form.get('d')?.setValue(selectedDevice.exponential_function.d);
    }

    if (this.errorFunctionOptionList.length === 1) {
      this.form.get('selected_error_function')?.disable();
    } else {
      this.form.get('selected_error_function')?.enable();
    }

    this.setLock(selectedDevice);
    this.setLock2(selectedDevice, this.equipmentDeviceList);

    this.onChange.emit({
      data: this.form.value,
      category: selectedDevice.category,
      name: selectedDevice.name,
    });
    this.isShowErrorFunction = true;
  }

  public setValidationOnExponential(device: any): void {
    let min_a: number = 0;
    let max_a: number = 0;
    let min_b: number = 0;
    let max_b: number = 0;
    let min_c: number = 0;
    let max_c: number = 0;
    let min_d: number = 0;
    let max_d: number = 0;

    if (device.category === TEST_EQUIPMENT_DEVICE_LIST.LACTATE_METER && device.name.includes('Lactate Pro 1') && device.base.includes('YSI 1500')) {
      min_a = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_A_MIN;
      max_a = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_A_MAX;
      min_b = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_B_MIN;
      max_b = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_B_MAX;
      min_c = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_C_MIN;
      max_c = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_C_MAX;
      min_d = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_D_MIN;
      max_d = DEVICE_TYPE_EXPONENTIAL_RANGE_ENUM.LACTATE_PRO_1_D_MAX;
    }

    this.form.get('a')?.setValidators([Validators.required, Validators.min(min_a), Validators.max(max_a)]);
    this.form.get('b')?.setValidators([Validators.required, Validators.min(min_b), Validators.max(max_b)]);
    this.form.get('c')?.setValidators([Validators.required, Validators.min(min_c), Validators.max(max_c)]);
    this.form.get('d')?.setValidators([Validators.required, Validators.min(min_d), Validators.max(max_d)]);
  }

  public setValidationOnLinear(device: any): void {
    let min_a: number = 0;
    let max_a: number = 0;
    let min_b: number = 0;
    let max_b: number = 0;

    if (device.category === TEST_EQUIPMENT_DEVICE_LIST.GPS_DEVICE) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.LACTATE_METER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VO2_ANALYZER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.POWER_METER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_A_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_B_OFFSET_MAX;
    }

    this.form.get('a_slope')?.setValidators([Validators.required, Validators.min(min_a), Validators.max(max_a)]);
    this.form.get('b_offset')?.setValidators([Validators.required, Validators.min(min_b), Validators.max(max_b)]);
  }

  public setValidationOnLinear2(device: any): void {
    let min_a: number = 0;
    let max_a: number = 0;
    let min_b: number = 0;
    let max_b: number = 0;

    if (device.category === TEST_EQUIPMENT_DEVICE_LIST.GPS_DEVICE) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.GPS_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.LACTATE_METER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.LACTATE_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VCO2_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.VO2_ANALYZER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_A_SLOPE_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.VO2_B_OFFSET_MAX;
    } else if (device.category === TEST_EQUIPMENT_DEVICE_LIST.POWER_METER) {
      min_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_A_SLOPE_MIN;
      max_a = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_A_MAX;
      min_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_B_OFFSET_MIN;
      max_b = DEVICE_TYPE_LINEAR_RANGE_ENUM.POWER_B_OFFSET_MAX;
    }

    this.form.get('a_slope2')?.setValidators([Validators.required, Validators.min(min_a), Validators.max(max_a)]);
    this.form.get('b_offset2')?.setValidators([Validators.required, Validators.min(min_b), Validators.max(max_b)]);
  }

  public onErrorFunctionSelection(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);

    if (event.value === ERROR_FUNCTION_TYPE.LINEAR) {
      this.form.get('a_slope')?.setValue(selectedDevice.linear_function.a);
      this.form.get('b_offset')?.setValue(selectedDevice.linear_function.b);
    } else if (event.value === ERROR_FUNCTION_TYPE.EXPONENTIAL) {
      this.form.get('a')?.setValue(selectedDevice.exponential_function.a);
      this.form.get('b')?.setValue(selectedDevice.exponential_function.b);
      this.form.get('c')?.setValue(selectedDevice.exponential_function.c);
      this.form.get('d')?.setValue(selectedDevice.exponential_function.d);
    }

    this.onChange.emit({
      data: this.form.value,
      category: selectedDevice.category,
      name: selectedDevice.name,
    });
  }

  public getAllReferencesFromDevices(selectedDevice: any, secondDevice: any): any {
    const uniqueUrl: string[] = [];
    const resultReferenceArray: any = [...selectedDevice.reference, ...secondDevice.reference];

    return resultReferenceArray.filter((reference: any) => {
      if (!uniqueUrl.includes(reference.url)) {
        uniqueUrl.push(reference.url);

        return reference;
      }
    });
  }

  public setReferences(originalDeviceList: any, selectedDevice: any): void {
    const secondDevice: any = originalDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);
    if (secondDevice) {
      this.referenceArray = this.getAllReferencesFromDevices(selectedDevice, secondDevice);
    } else {
      this.referenceArray = selectedDevice.reference;
    }
  }

  public setAccessibilityInputs(config: any): void {
    if (config.lock) {
      this.disableInputs();
    } else {
      this.enableInputs();
    }
  }

  public setAccessibilityInputs2(config: any): void {
    if (config.lock) {
      this.disableInputs2();
    } else {
      this.enableInputs2();
    }
  }

  public getDeviceById(config: any, id: number): any {
    this.currentDeviceId = id;
    return config.comboBoxDeviceList.find((device: any) => device.id === id);
  }

  public setErrorFunctionOptionList(config: any, id: number): void {
    const currentDevice: any = this.getDeviceById(config, id);
    if (currentDevice) {
      const errorList: any[] = [];

      if (currentDevice.hasOwnProperty('exponential_function') &&
        currentDevice.exponential_function &&
        currentDevice.exponential_function.hasOwnProperty('a')) {
        errorList.push({ value: 2, name: 'Exponential' });
      }

      if (currentDevice.hasOwnProperty('linear_function') &&
        currentDevice.linear_function &&
        currentDevice.linear_function.hasOwnProperty('a')) {
        errorList.push({ value: 1, name: 'Linear' });
      }

      this.errorFunctionOptionList = errorList;
    }
  }

  public setErrorFunctionOptionList2(config: any, id: number): void {
    const currentDevice: any = this.getDeviceById(config, id);
    if (currentDevice) {
      const errorList: any[] = [];

      if (currentDevice.hasOwnProperty('exponential_function') &&
        currentDevice.exponential_function &&
        currentDevice.exponential_function.hasOwnProperty('a')) {
        errorList.push({ value: 2, name: 'Exponential' });
      }

      if (currentDevice.hasOwnProperty('linear_function') &&
        currentDevice.linear_function &&
        currentDevice.linear_function.hasOwnProperty('a')) {
        errorList.push({ value: 1, name: 'Linear' });
      }

      this.errorFunctionOptionList2 = errorList;
    }
  }

  public trackSelectedDevice(): void {
    this.form.get('selected_device')?.valueChanges
      .subscribe({
        next: () => {
          this.setValueToForm(this.config);
        }
      })
  }

  public setValueToForm(config: any): void {
    if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      const comboBox: AbstractControl<any> | null = this.form.get('id');
      const deviceSelect: AbstractControl<any> | null = this.form.get('selected_device');

      if (deviceSelect?.value === TEST_EQUIPMENT_DEVICE_SELECTION.DEFAULT) {
        comboBox?.setValue(0);
        comboBox?.disable();
        this.optionList = this.config.defaultDevice;
      } else if (deviceSelect?.value === TEST_EQUIPMENT_DEVICE_SELECTION.ATHLETE) {
        comboBox?.setValue(0);
        comboBox?.disable();
        this.optionList = this.config.defaultAthleteDevice;
      } else if (deviceSelect?.value === TEST_EQUIPMENT_DEVICE_SELECTION.ANOTHER) {
        if (config.comboBoxSelection === null) {
          comboBox?.reset();
        }
        comboBox?.setValue(config.comboBoxSelection);
        comboBox?.enable();
        this.optionList = this.config.anotherOptionList;
      }
    }
  }

  public hasSecondErrorFunction(deviceList: any[], selectedDevice: any): any {
    let matchCounter: number = 0;
    const deviceName: string = selectedDevice.name;

    deviceList.forEach((device: any) => {
      if (device.name === deviceName) {
        ++matchCounter;
      }
    });

    return matchCounter > 1;
  }

  public initFormControlsBasedOnDeviceType(selectedDevice: any): any {
    let form: any = {
      id: ['', Validators.required],
      selected_error_function: ['', Validators.required],
      organization: [''],
      a_slope: ['', Validators.required],
      b_offset: ['', Validators.required],
      a: ['', Validators.required],
      b: ['', Validators.required],
      c: ['', Validators.required],
      d: ['', Validators.required],
      id2: ['', Validators.required],
      selected_error_function2: ['', Validators.required],
      a_slope2: ['', Validators.required],
      b_offset2: ['', Validators.required],
      a2: ['', Validators.required],
      b2: ['', Validators.required],
      c2: ['', Validators.required],
      d2: ['', Validators.required],
      organization_default: [false],
      athlete_default: [false],
    };
    return form;
  }

  public initForm(selectedDevice: any): void {
    if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      this.form = this.fb.group({
        selected_device: ['', Validators.required],
        ...this.initFormControlsBasedOnDeviceType(selectedDevice),
      });
    } else {
      this.form = this.fb.group(this.initFormControlsBasedOnDeviceType(selectedDevice));
    }
  }

  public setComboboxOptionList(config: any, selectedRadioButton?: number): void {
    const comboBox: AbstractControl<any> | null = this.form.get('id');

    if (selectedRadioButton === TEST_EQUIPMENT_DEVICE_SELECTION_VALUE.ATHLETE) {
      comboBox?.disable();
    } else if (selectedRadioButton === TEST_EQUIPMENT_DEVICE_SELECTION_VALUE.ORGANIZATION) {
      comboBox?.disable();
    } else {
      comboBox?.enable();
    }
    this.optionList = config.comboBoxDeviceList;
  }

  public getSelectedDevice(config: any, radioOption: number = 0): any {
    const deviceList: any[] = config.comboBoxDeviceList;

    if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.ATHLETE_CREATION) {
      // Show athlete default
      const selectedDevice = config.selectedDevice;
      if (selectedDevice != null) {
        const selectedDevice = config.selectedDevice;
        let object: any = deviceList.find((device: any) => device.id === selectedDevice);
        return object ? object : '';
      }
    } else if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      if (radioOption === 0) {
        // Athlete default
        const athleteDefault = config.athleteDefault;
        if (athleteDefault != null) {
          const athleteDefault = config.athleteDefault;
          let object: any = deviceList.find((device: any) => device.id === athleteDefault);
          return object ? object : '';
        } else {
          return ''
        }
      } else if (radioOption === 1) {
        // Organization default
        const organizationDefault = config.organizationDefault;
        if (organizationDefault != null) {
          const organizationDefault = config.organizationDefault;
          let object: any = deviceList.find((device: any) => device.id === organizationDefault);
          return object ? object : '';
        } else {
          return ''
        }
      } else {
        // No default selection
        return '';
      }
    } else {
      // Organization default for the profile page
      const organizationDefault = config.organizationDefault;
      if (organizationDefault != null) {
        const organizationDefault = config.organizationDefault;
        let object: any = deviceList.find((device: any) => device.id === organizationDefault);
        return object ? object : '';
      } else {
        return ''
      }
    }
  }

  public onErrorFunctionChanged(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);

    if (event.value === ERROR_FUNCTION_TYPE.LINEAR) {
      this.form.get('a_slope')?.setValue(selectedDevice.linear_function.a);
      this.form.get('b_offset')?.setValue(selectedDevice.linear_function.b);
    } else if (event.value === ERROR_FUNCTION_TYPE.EXPONENTIAL) {
      this.form.get('a')?.setValue(selectedDevice.exponential_function.a);
      this.form.get('b')?.setValue(selectedDevice.exponential_function.b);
      this.form.get('c')?.setValue(selectedDevice.exponential_function.c);
      this.form.get('d')?.setValue(selectedDevice.exponential_function.d);
    }

    this.onChange.emit({
      data: this.form.value,
      category: selectedDevice.category,
      name: selectedDevice.name,
    });
  }

  public onErrorFunctionChanged2(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);
    const secondDevice: any = this.equipmentDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);

    if (secondDevice) {
      if (event.value === ERROR_FUNCTION_TYPE.LINEAR) {
        this.form.get('a_slope2')?.setValue(secondDevice.linear_function.a);
        this.form.get('b_offset2')?.setValue(secondDevice.linear_function.b);
      } else if (event.value === ERROR_FUNCTION_TYPE.EXPONENTIAL) {
        this.form.get('a2')?.setValue(secondDevice.exponential_function.a);
        this.form.get('b2')?.setValue(secondDevice.exponential_function.b);
        this.form.get('c2')?.setValue(secondDevice.exponential_function.c);
        this.form.get('d2')?.setValue(secondDevice.exponential_function.d);
      }

      this.onChange.emit({
        data: this.form.value,
        category: secondDevice.category,
        name: selectedDevice.name,
      });
    }
  }

  public setDefaultLinearValue(device: any): void {
    this.linearDefaultValue = device.parent_linear_function ?? device.linear_function;
  }

  public setDefaultLinearValue2(device: any): void {
    this.linearDefaultValue2 = device.parent_linear_function ?? device.linear_function;
  }

  public getDefaultLinearValue(): any {
    return this.linearDefaultValue;
  }

  public getDefaultLinearValue2(): any {
    return this.linearDefaultValue2;
  }

  public setDefaultExponentialValue(device: any): void {
    this.exponentialDefaultValue = device.parent_exponential_function ?? device.exponential_function;
  }

  public setDefaultExponentialValue2(device: any): void {
    this.exponentialDefaultValue2 = device.parent_exponential_function ?? device.exponential_function;
  }

  public getDefaultExponentialValue(): any {
    return this.exponentialDefaultValue;
  }

  public getDefaultExponentialValue2(): any {
    return this.exponentialDefaultValue2;
  }

  public setSingleFormValue(selectedDevice: any): void {
    const hasLinearFunction = selectedDevice.hasOwnProperty('linear_function') && selectedDevice.linear_function;
    const hasExponentialFunction = selectedDevice.hasOwnProperty('exponential_function') && selectedDevice.exponential_function;

    if (selectedDevice.hasOwnProperty('linear_function')) {
      this.setDefaultLinearValue(selectedDevice);
    }

    if (selectedDevice.hasOwnProperty('exponential_function')) {
      this.setDefaultExponentialValue(selectedDevice);
    }

    if (selectedDevice) {
      this.setErrorFunctionOptionList(this.config, selectedDevice.id);
      this.form.get('id')?.setValue(selectedDevice.id);
      this.form.get('selected_error_function')?.setValue(selectedDevice.selected_error_function);

      if (hasLinearFunction) {
        this.form.get('a_slope')?.setValue(selectedDevice.linear_function.a);
        this.form.get('b_offset')?.setValue(selectedDevice.linear_function.b);
      }
      if (hasExponentialFunction) {
        this.form.get('a')?.setValue(selectedDevice.exponential_function.a);
        this.form.get('b')?.setValue(selectedDevice.exponential_function.b);
        this.form.get('c')?.setValue(selectedDevice.exponential_function.c);
        this.form.get('d')?.setValue(selectedDevice.exponential_function.d);
      }

      if (this.errorFunctionOptionList.length === 1) {
        this.form.get('selected_error_function')?.disable();
      }

      this.form.get('organization')?.setValue(selectedDevice.is_organization_equipment);
      this.isShowErrorFunction = true;
    }
  }

  public setSecondFormValue(originalDeviceList: any[], selectedDevice: any): void {
    const secondDevice: any = originalDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);
    const hasLinearFunction = selectedDevice.hasOwnProperty('linear_function') && selectedDevice.linear_function;
    const hasExponentialFunction = selectedDevice.hasOwnProperty('exponential_function') && selectedDevice.exponential_function;

    if (secondDevice) {
      if (secondDevice.hasOwnProperty('linear_function')) {
        this.setDefaultLinearValue2(secondDevice);
      }

      if (secondDevice.hasOwnProperty('exponential_function')) {
        this.setDefaultExponentialValue(secondDevice);
      }

      if (secondDevice) {
        this.setErrorFunctionOptionList2({comboBoxDeviceList: originalDeviceList}, secondDevice.id);
        this.form.get('id2')?.setValue(secondDevice.id);
        this.form.get('selected_error_function2')?.setValue(secondDevice.selected_error_function);

        if (hasLinearFunction) {
          this.form.get('a_slope2')?.setValue(secondDevice.linear_function.a);
          this.form.get('b_offset2')?.setValue(secondDevice.linear_function.b);
        }
        if (hasExponentialFunction) {
          this.form.get('a2')?.setValue(secondDevice.exponential_function.a);
          this.form.get('b2')?.setValue(secondDevice.exponential_function.b);
          this.form.get('c2')?.setValue(secondDevice.exponential_function.c);
          this.form.get('d2')?.setValue(secondDevice.exponential_function.d);
        }

        if (this.errorFunctionOptionList2.length === 1) {
          this.form.get('selected_error_function2')?.disable();
        } else {
          this.form.get('selected_error_function2')?.enable();
        }

        this.isShowErrorFunction2 = true;
      }
    }
  }

  private clearFormValues(config: any): void {
    this.isShowErrorFunction = false;
    this.isShowErrorFunction2 = false;
    this.optionList = config.comboBoxDeviceList;
    const comboBox: AbstractControl<any> | null = this.form.get('id');
    comboBox?.enable();
    comboBox?.setValue(0);
  }

  public setFormValue(config: any, radioOption: number = 0): void {
    const selectedDevice: any = this.getSelectedDevice(config, radioOption);
    let hasSecondErrorFunction: any = false;

    if (radioOption === 2) {
      this.clearFormValues(config);
      this.setDeviceSelectionList(config);
    }

    if (selectedDevice) {
      hasSecondErrorFunction = this.hasSecondErrorFunction(this.equipmentDeviceList, selectedDevice)

      if (hasSecondErrorFunction) {
        this.setSecondFormValue(this.equipmentDeviceList, selectedDevice);
      }

      this.setSingleFormValue(selectedDevice);
    }
    this.setDeviceSelectionList(config);
  }
  
  public setLock(selectedDevice?: any): void {
    if (!this.selectedDevice) {
      return
    }

    this.isLock = selectedDevice.selected_error_function_override;
    if (this.isLock) {
      this.enableInputs();
    } else {
      this.disableInputs();
    }
  }

  public setLock2(selectedDevice?: any, originalDeviceList?: any[]): void {
    if (!this.selectedDevice) {
      return
    }

    const validDeviceList = originalDeviceList != null && Array.isArray(originalDeviceList) && originalDeviceList.length > 0;
    if (!validDeviceList) {
      return
    }

    const secondDevice: any = originalDeviceList.find((device: any) => device.name === selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);
    if (!secondDevice) {
      return
    }

    this.isLock2 = secondDevice.selected_error_function_override;
    if (this.isLock2) {
      this.enableInputs2();
    } else {
      this.disableInputs2();
    }
  }

  public setDeviceSelectionList(config: any): void {
    const deviceSelectionList = [];
    for (let i = 0; i < this.deviceSelectionList.length; i++) {
      const newListItem = {...this.deviceSelectionList[i]};
      const selectedDevice: any = this.getSelectedDevice(config, i);
      if (selectedDevice) {
        newListItem.subName = selectedDevice.name;
        newListItem.disabled = false;
      }
      deviceSelectionList.push(newListItem);
    }
    this.deviceSelectionList = deviceSelectionList;
  }

  public initOptionAndValueForm(config: any, selectedRadioButton: number): void {
    if (this.context === TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      this.form.get('selected_device')?.setValue(selectedRadioButton);
      this.updateDefaultsDisabledState(selectedRadioButton);
      this.setComboboxOptionList(config, selectedRadioButton)
      this.setFormValue(config, selectedRadioButton);
    } else {
      this.setComboboxOptionList(config)
      this.setFormValue(config);
    }
  }

  public disableInputs(): void {
    this.form.get('a_slope')?.disable();
    this.form.get('b_offset')?.disable();
    this.form.get('a')?.disable();
    this.form.get('b')?.disable();
    this.form.get('c')?.disable();
    this.form.get('d')?.disable();
  }

  public disableInputs2(): void {
    this.form.get('a_slope2')?.disable();
    this.form.get('b_offset2')?.disable();
    this.form.get('a2')?.disable();
    this.form.get('b2')?.disable();
    this.form.get('c2')?.disable();
    this.form.get('d2')?.disable();
  }

  public enableInputs(): void {
    this.form.get('a_slope')?.enable();
    this.form.get('b_offset')?.enable();
    this.form.get('a')?.enable();
    this.form.get('b')?.enable();
    this.form.get('c')?.enable();
    this.form.get('d')?.enable();
  }

  public enableInputs2(): void {
    this.form.get('a_slope2')?.enable();
    this.form.get('b_offset2')?.enable();
    this.form.get('a2')?.enable();
    this.form.get('b2')?.enable();
    this.form.get('c2')?.enable();
    this.form.get('d2')?.enable();
  }

  public onLockChanged(event: any): void {
    let revertToDefault = null;
    if (!event) {
      this.disableInputs();
      this.revertInputsToDefaultValue();
      revertToDefault = this.selectedDevice.parent_id;
    } else {
      this.enableInputs();
    }

    if (this.selectedDevice) {
      this.onChange.emit({
        data: {...this.form.value, revertToDefault},
        category: this.selectedDevice.category,
        name: this.selectedDevice.name,
      });
    }
  }

  public onLockChanged2(event: any): void {
    let revertToDefault = null;

    const originalDeviceList = this.equipmentDeviceList;
    const validDeviceList = originalDeviceList != null && Array.isArray(originalDeviceList) && originalDeviceList.length > 0;
    if (!validDeviceList) {
      return
    }

    const secondDevice: any = originalDeviceList.find((device: any) => device.name === this.selectedDevice.name && device.category === TEST_EQUIPMENT_DEVICE_LIST.VCO2_ANALYZER);
    if (!secondDevice) {
      return
    }

    if (!event) {
      this.disableInputs2();
      this.revertInputsToDefaultValue2();
      revertToDefault = secondDevice.parent_id;
    } else {
      this.enableInputs2();
    }

    if (secondDevice) {
      this.onChange.emit({
        data: {...this.form.value, revertToDefault},
        category: secondDevice.category,
        name: secondDevice.name,
      });
    }
  }

  public revertInputsToDefaultValue(): void {
    const linear: any = this.getDefaultLinearValue();
    const exponential: any = this.getDefaultExponentialValue();
    if (linear) {
      this.form.get('a_slope')?.setValue(linear.a);
      this.form.get('b_offset')?.setValue(linear.b);
    }
    if (exponential) {
      this.form.get('a')?.setValue(exponential.a);
      this.form.get('b')?.setValue(exponential.b);
      this.form.get('c')?.setValue(exponential.c);
      this.form.get('d')?.setValue(exponential.d);
    }
  }

  public revertInputsToDefaultValue2(): void {
    const linear: any = this.getDefaultLinearValue2();
    const exponential: any = this.getDefaultExponentialValue2();

    if (linear) {
      this.form.get('a_slope2')?.setValue(linear.a);
      this.form.get('b_offset2')?.setValue(linear.b);
    }
    if (exponential) {
      this.form.get('a2')?.setValue(exponential.a);
      this.form.get('b2')?.setValue(exponential.b);
      this.form.get('c2')?.setValue(exponential.c);
      this.form.get('d2')?.setValue(exponential.d);
    }
  }

  public selectDefaultDevices(event: any) {
    this.setFormValue(this.config, event.value);
    this.updateDefaultsDisabledState(event.value as number);
  }

  public showRadioButtons(): boolean {
    if (this.context !== TEST_EQUIPMENT_CONTEXT_ENUM.TEST_CREATION) {
      return false
    }
    return this.showOrganizationDefaultCheckbox() || this.showAthleteDefaultCheckbox();
  }

  public showOrganizationDefaultCheckbox(): boolean {
    return this.sessionService?.rights?.admin_org_update ?? false;
  }

  public showAthleteDefaultCheckbox(): boolean {
    const hasRights = this.sessionService?.rights?.athletes_update ?? false;
    const isAthleteCategory = ATHLETE_EQUIPMENT_CATEGORIES.includes(this.config?.deviceCategory ?? "Uncategorized");
    return hasRights && isAthleteCategory;
  }

  public toggleAthleteDefault(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);
    if (!selectedDevice) {
      return
    }

    const deviceData = this.form.value;
    deviceData.id = this.form.get('id')?.value;
    this.onChange.emit({
      data: deviceData,
      category: selectedDevice.category,
      name: selectedDevice.name,
    });
  }

  public toggleorganizationDefault(event: any): void {
    const selectedDevice: any = this.getDeviceById(this.config, this.currentDeviceId);
    if (!selectedDevice) {
      return
    }

    const deviceData = this.form.value;
    deviceData.id = this.form.get('id')?.value;
    this.onChange.emit({
      data: deviceData,
      category: selectedDevice.category,
      name: selectedDevice.name,
    });
  }

  public disableAthleteDefault(): boolean {
    return this.form.get('selected_device')?.value === 0;
  }

  public disableOrganizationDefault(): boolean {
    return this.form.get('selected_device')?.value === 1;
  }

  public get errorFunctionType(): number {
    return this.form.get('selected_error_function')?.value;
  }

  public get errorFunctionType2(): number {
    return this.form.get('selected_error_function2')?.value;
  }

  private updateDefaultsDisabledState(selectedRadioButton: number): void {
    if (selectedRadioButton === 0) {
      this.form.get('athlete_default')?.disable();
      this.form.get('organization_default')?.enable();
    } else if (selectedRadioButton === 1) {
      this.form.get('athlete_default')?.enable();
      this.form.get('organization_default')?.disable();
    } else {
      this.form.get('athlete_default')?.enable();
      this.form.get('organization_default')?.enable();
    }
  }

  public get lock(): boolean {
    return this.isLock;
  }

  public get a_slope(): AbstractControl<any, any> | null {
    return this.form.get('a_slope');
  }

  public get a_slope2(): AbstractControl<any, any> | null {
    return this.form.get('a_slope2');
  }

  public get b_offset(): AbstractControl<any, any> | null {
    return this.form.get('b_offset');
  }

  public get b_offset2(): AbstractControl<any, any> | null {
    return this.form.get('b_offset2');
  }

  public get a2(): AbstractControl<any, any> | null {
    return this.form.get('a2');
  }

  public get b2(): AbstractControl<any, any> | null {
    return this.form.get('b2');
  }

  public get c2(): AbstractControl<any, any> | null {
    return this.form.get('c2');
  }

  public get d2(): AbstractControl<any, any> | null {
    return this.form.get('d2');
  }

  public get a(): AbstractControl<any, any> | null {
    return this.form.get('a');
  }

  public get b(): AbstractControl<any, any> | null {
    return this.form.get('b');
  }

  public get c(): AbstractControl<any, any> | null {
    return this.form.get('c');
  }

  public get d(): AbstractControl<any, any> | null {
    return this.form.get('d');
  }
}
